import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, ExternalLink } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { itemsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const fmt = (n) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Badge colour per source. Greens/teals = stock in, red/amber/rose = stock out.
const SOURCE_STYLES = {
  purchase: 'bg-green-100 text-green-800',
  purchase_return: 'bg-amber-100 text-amber-800',
  sale: 'bg-blue-100 text-blue-800',
  sale_return: 'bg-purple-100 text-purple-800',
  exchange_returned: 'bg-teal-100 text-teal-800',
  exchange_given: 'bg-rose-100 text-rose-800',
  damage: 'bg-orange-100 text-orange-800',
};

const describe = (e) => {
  const q = fmt(Math.abs(e.quantity));
  switch (e.source_key) {
    case 'purchase':
      return `Purchased ${q}${e.note ? ' (draft — not applied)' : ''}`;
    case 'purchase_return':
      return `Returned ${q} to supplier`;
    case 'sale':
      return `Sold ${q}`;
    case 'sale_return':
      return `Customer returned ${q}`;
    case 'exchange_returned':
      return `Customer returned ${q} (exchange)`;
    case 'exchange_given':
      return `Given ${q} to customer (exchange)`;
    case 'damage':
      return `Marked ${q} damaged (${e.status}) · ${e.note}`;
    default:
      return e.source;
  }
};

// Each source's detail route (note the inconsistent pluralisation across
// modules), so the document number can deep-link to its record in a new tab.
const DETAIL_PATH = {
  purchase: (id) => `/purchases/${id}`,
  purchase_return: (id) => `/purchase-returns/${id}`,
  sale: (id) => `/sales/${id}`,
  sale_return: (id) => `/sale-return/${id}`,
  exchange_returned: (id) => `/exchange/${id}`,
  exchange_given: (id) => `/exchange/${id}`,
  // ref_id is the dispatch (null until sent). Its page only exists where damages is switched on.
  damage: (id, features) => (features?.damages ? `/damages/dispatches/${id}` : null),
};

const docHref = (e, features) => {
  const build = DETAIL_PATH[e.source_key];
  return build && e.ref_id != null ? build(e.ref_id, features) : null;
};

const SummaryCard = ({ label, value, valueClass }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
    <div className="text-sm text-gray-500">{label}</div>
    <div className={`text-2xl font-semibold ${valueClass || 'text-gray-900'}`}>{value}</div>
  </div>
);

const ItemStockHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const features = useAppStore((s) => s.features);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await itemsAPI.getStockHistory(id);
        if (active) setData(res.data);
      } catch (err) {
        if (active) setError(err.response?.data?.detail || 'Failed to load stock history');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const events = useMemo(() => data?.events || [], [data]);

  // Running-balance line: a leading "Start = 0" point, then the balance after
  // each movement. Point colour encodes direction (green in, red out).
  const chart = useMemo(() => {
    if (!events.length) return null;
    return {
      labels: ['Start', ...events.map((e) => (e.date ? dayjs(e.date).format('DD MMM') : '—'))],
      values: [0, ...events.map((e) => e.running)],
      colors: ['#9ca3af', ...events.map((e) => (e.delta > 0 ? '#16a34a' : e.delta < 0 ? '#dc2626' : '#9ca3af'))],
      meta: [null, ...events],
    };
  }, [events]);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/items')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" /> Back to items
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { item, summary } = data;

  const chartData = chart && {
    labels: chart.labels,
    datasets: [
      {
        data: chart.values,
        stepped: true,
        borderColor: '#94a3b8',
        borderWidth: 2,
        pointBackgroundColor: chart.colors,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1.5,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const m = chart.meta[items[0].dataIndex];
            return m ? m.source : 'Opening';
          },
          label: (ctx) => {
            const m = chart.meta[ctx.dataIndex];
            if (!m) return 'Stock: 0';
            const sign = m.delta > 0 ? '+' : '';
            return `${m.doc} · ${sign}${fmt(m.delta)} · stock ${fmt(ctx.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Stock on hand' } },
      x: { grid: { display: false }, ticks: { maxRotation: 45, autoSkip: true } },
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock history</h1>
          <p className="text-gray-600">
            {item.name}
            {item.barcode ? ` • Barcode: ${item.barcode}` : ''}
            {item.brand ? ` • ${item.brand}` : ''}
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Current stock" value={`${fmt(summary.current_stock)}${item.unit ? ` ${item.unit}` : ''}`} />
        <SummaryCard label="Total in" value={`+${fmt(summary.total_in)}`} valueClass="text-green-600" />
        <SummaryCard label="Total out" value={`−${fmt(summary.total_out)}`} valueClass="text-red-600" />
        <SummaryCard label="Movements" value={fmt(summary.event_count)} />
      </div>

      {!summary.reconciles && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Recorded movements net to {fmt(summary.net_change)}, but current stock is {fmt(summary.current_stock)}.
            Unexplained adjustment: {summary.unexplained_adjustment > 0 ? '+' : ''}
            {fmt(summary.unexplained_adjustment)} — likely an opening balance or a manual/website stock edit made outside
            these modules.
          </span>
        </div>
      )}

      {/* Chart */}
      {chartData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stock over time</h2>
          <div style={{ height: 280 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">What happened</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Stock after</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No stock movements recorded for this item.
                  </td>
                </tr>
              ) : (
                events.map((e, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {e.date ? dayjs(e.date).format('DD MMM YYYY') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${SOURCE_STYLES[e.source_key] || 'bg-gray-100 text-gray-800'}`}>
                        {e.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{describe(e)}</td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {docHref(e, features) ? (
                        <a
                          href={docHref(e, features)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {e.doc}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-gray-500">{e.doc}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                      <span className={e.delta > 0 ? 'text-green-600 font-medium' : e.delta < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}>
                        {e.delta > 0 ? '+' : ''}
                        {fmt(e.delta)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                      {fmt(e.running)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ItemStockHistory;
